// Campaign audience picker — the two axes an audience is made of.
//
// The filter select says how a campaign narrows; the scope radios say
// which contact tables it reaches. The two are not fully independent:
// the stage filters name a pipeline position and a client has left the
// pipeline, so choosing one locks the scope to prospects. The lock is
// rendered, not hidden — a control that disappears reads as a missing
// feature, one that greys out reads as a rule.
//
// Which filters can choose a scope comes from a data attribute rather
// than being restated here. The server owns that rule; a copy in this
// file would drift silently, and the database CHECK is the only place
// the drift would surface.
//
// The tag field narrows whichever audience is chosen, to the tagged
// contacts or to everyone but them, as the switch above it says. It is
// a chip list over a hidden comma-joined input. The server canonicalises
// through validate.Tags and stores the same joined form, so the chips
// and the submitted value never disagree.
//
// A "Gains a tag" trigger picks its own tags with the same chip list,
// bound on its own [data-trigger-tag-field] root.
//
// Targets: [data-audience-field], [data-audience-scope], [data-tag-chips],
// [data-trigger-tag-field].

import { onceGuard } from "./bind_once.js";

const firstBind = onceGuard();

const SCOPE_PROSPECTS = "prospects";

export function initCampaignAudience(root = document) {
  root
    .querySelectorAll("[data-audience-field], [data-trigger-tag-field]")
    .forEach((field) => {
      if (!firstBind(field)) return;

      const select = field.querySelector('select[name="audience"]');
      if (select) bindScope(field, select);
      bindTags(field);
    });
}

function bindScope(field, select) {
  const group = field.querySelector("[data-audience-scope]");
  if (!group) return;

  const radios = group.querySelectorAll('input[name="audience_scope"]');
  const note = group.querySelector("[data-scope-locked-note]");
  const choosable = (group.dataset.scopeChoosable || "").split(/\s+/);

  // A locked filter reaches prospects only; an unlocked one keeps
  // whatever scope the agent picked, so an edit opens on the saved scope.
  const sync = () => {
    const locked = !choosable.includes(select.value);

    radios.forEach((radio) => {
      // The prospects radio stays enabled so the locked state still
      // submits — a disabled radio contributes no value at all.
      radio.disabled = locked && radio.value !== SCOPE_PROSPECTS;
      if (locked) radio.checked = radio.value === SCOPE_PROSPECTS;
    });

    if (note) note.classList.toggle("hidden", !locked);
  };

  select.addEventListener("change", sync);
  sync();
}

function bindTags(field) {
  const chips = field.querySelector("[data-tag-chips]");
  const template = field.querySelector("template[data-tag-chip-template]");
  if (!chips || !template) return;

  const entry = chips.querySelector("[data-tag-entry]");
  const value = field.querySelector("[data-tag-value]");
  const ceiling = Number(chips.dataset.tagCeiling) || 10;
  const maxLength = Number(chips.dataset.tagMaxLength) || 40;
  if (!entry || !value) return;

  const read = () => splitTags(value.value);

  const write = (tags) => {
    value.value = tags.join(", ");

    chips.querySelectorAll("[data-tag-chip]").forEach((chip) => chip.remove());
    tags.forEach((tag) => chips.insertBefore(buildChip(template, tag), entry));

    // Past the ceiling the server rejects the submission, so stop taking
    // input. readOnly rather than disabled: disabling a focused field
    // blurs it, which would throw focus to the body mid-typing and take
    // Backspace — the only keyboard way back under the ceiling — with it.
    const full = tags.length >= ceiling;
    entry.readOnly = full;
    entry.placeholder = full ? "Tag limit reached" : "Add a tag";
  };

  // Mirrors validate.Tag: lower-case, collapse internal whitespace, and
  // refuse what the server refuses. Both halves matter — a tag stored in
  // any other form would never match the filter, and one the server drops
  // would sit here as a chip describing a cohort nobody is in. The length
  // comes from the server so the two cannot drift.
  const canonical = (raw) => {
    const tag = raw.trim().toLowerCase().split(/\s+/).join(" ");
    if (!tag || [...tag].length > maxLength) return "";

    return tag;
  };

  // Splits on the comma first, exactly as validate.Tags does. A pasted
  // "students,alumni" is two tags to the server, so it has to be two
  // chips here — one chip holding a comma would submit a value the row it
  // represents does not match, and its remove button would find nothing
  // to remove.
  const add = (raw) => {
    const parts = splitTags(raw);
    if (parts.length === 0) return false;

    const tags = read();
    let accepted = true;

    for (const part of parts) {
      const tag = canonical(part);
      if (!tag) {
        accepted = false;

        continue;
      }
      if (tags.length >= ceiling || tags.includes(tag)) continue;

      tags.push(tag);
    }
    write(tags);

    return accepted;
  };

  chips.addEventListener("click", (event) => {
    const remove = event.target.closest("[data-tag-remove]");
    if (remove) {
      write(read().filter((tag) => tag !== remove.dataset.tagRemove));
      // write() destroyed the button that held focus, so a keyboard user
      // would land on the body and have to tab back from the top.
      entry.focus();

      return;
    }
    if (event.target === chips) entry.focus();
  });

  entry.addEventListener("keydown", (event) => {
    if (event.key === "Enter" || event.key === ",") {
      // Enter inside a form submits it; a tag is not a submission.
      event.preventDefault();
      // Text the server would drop stays in the field rather than
      // becoming a chip for a cohort nobody is in.
      if (add(entry.value)) entry.value = "";

      return;
    }

    // Backspace on an empty field removes the last chip — the standard
    // gesture for this control, and the only way to reach the chips
    // without leaving the keyboard.
    if (event.key === "Backspace" && entry.value === "") {
      const tags = read();
      if (tags.length > 0) write(tags.slice(0, -1));
    }
  });

  const commit = () => {
    if (add(entry.value)) entry.value = "";
  };

  // Picking from the datalist fires change, not keydown.
  entry.addEventListener("change", commit);

  entry.addEventListener("blur", (event) => {
    // Pressing a chip's × blurs the entry before the click lands.
    // Committing here would rebuild every chip, destroying the button
    // between mousedown and mouseup so the click never fires and the ×
    // appears to do nothing.
    if (event.relatedTarget?.closest("[data-tag-remove]")) return;

    commit();
  });

  // The server already rendered the chips; only rebuild when they differ,
  // so an edit form does not flash its own content away and back.
  const rendered = [...chips.querySelectorAll("[data-tag-chip]")].map(
    (chip) => chip.dataset.tagChip,
  );
  if (rendered.join(",") !== read().join(",")) write(read());
}

function splitTags(raw) {
  return raw
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
}

function buildChip(template, tag) {
  const chip = template.content.firstElementChild.cloneNode(true);

  chip.dataset.tagChip = tag;
  chip.querySelector("[data-tag-label]").textContent = tag;

  const remove = chip.querySelector("[data-tag-remove]");
  remove.dataset.tagRemove = tag;
  remove.setAttribute("aria-label", `Remove tag ${tag}`);

  return chip;
}
